import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { useApi } from "@/shared/api/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type { PaginatedResponse, PaginationRequest } from "@/shared/types";
import type { User } from "@/entities/user";
import type { Manager } from "../model";
import type {
  AssignTeamleadToManagerRequest,
  AssignTeamleadToManagerResponse,
} from "./api.dto";


export const useAssignTeamleadToManager = () => {
  const { api } = useApi();

  return useMutation<
    AssignTeamleadToManagerResponse,
    AxiosError,
    AssignTeamleadToManagerRequest
  >({
    mutationKey: ["assignTeamleadToManager"],
    mutationFn: (data) => {
      return api.patch(
        `/managers/${encodeURI(data.managerId)}/teamlead/${encodeURI(data.teamleadId)}`,
      );
    },
  });
};

export const useGetAllManagers = (params: PaginationRequest) => {
  const { api } = useApi();

  return useQuery<PaginatedResponse<Manager>>({
    queryKey: ["managers", params],
    queryFn: async () => {
      const { data } = await api.get("/managers", {
        params: filterObjectKeys(params),
      });

      return data;
    },
  });
};

export const useGetManagersByTeamlead = (
  params: PaginationRequest & { userId: string },
) => {
  const { api } = useApi();
  const filteredParams = filterObjectKeys(params, ["userId"]);

  return useQuery<PaginatedResponse<Manager>>({
    queryKey: ["managersByTeamlead", filteredParams],
    queryFn: async () => {
      const { data } = await api.get(`/managers/teamlead/${params.userId}`, {
        params: filteredParams,
      });

      return data;
    },
  });
};

export const useGetManagersByMasterteamlead = (
  params: PaginationRequest & { userId: string; enabled?: boolean },
) => {
  const { api } = useApi();

  return useQuery<
    PaginatedResponse<Manager & { userId: User; teamlead: string }>
  >({
    queryKey: [
      "managersByMasterteamlead",
      filterObjectKeys(params, ["enabled"]),
    ],
    queryFn: async () => {
      const { data } = await api.get(
        `/managers/master-teamlead/${params.userId}`,
        {
          params: filterObjectKeys(params, ["userId", "enabled"]),
        },
      );

      return data;
    },
    enabled: params.enabled,
  });
};
